import React from 'react';

import { Inspector } from './Inspector';
import { useInspector } from './hooks/useInspector';
import type { Presentation } from './types';

import { useProjectById } from '@/hooks/projects/useProjectById';
import { useBrandGuidelinesDialogStore } from '@/stores/useBrandGuidelinesDialogStore';
import { useDocumentDialogStore } from '@/stores/useDocumentDialogStore';
import { useImageDialogStore } from '@/stores/useImageDialogStore';
import { useStrategyDialogStore } from '@/stores/useStrategyDialogStore';
import { useVideoDialogStore } from '@/stores/useVideoDialogStore';

/**
 * Single global Inspector driven by the `useInspector` store. Mounted once
 * in `AppLayout` so any caller can `inspector.openSubject(...)` without
 * mounting their own shell.
 *
 * Callers that need a `studioPanel` (e.g. CreateChatPage) register a
 * renderer via `setStudioPanelRenderer` — the mount invokes it for the
 * current top-of-stack subject and threads the result into PostBody.
 */
interface GlobalInspectorMountProps {
  presentationFilter?: Presentation | Presentation[];
}

function presentationMatchesFilter(
  presentation: Presentation,
  filter: GlobalInspectorMountProps['presentationFilter'],
): boolean {
  if (!filter) return true;
  return Array.isArray(filter) ? filter.includes(presentation) : filter === presentation;
}

export const GlobalInspectorMount: React.FC<GlobalInspectorMountProps> = ({
  presentationFilter,
}) => {
  const stack = useInspector((s) => s.stack);
  const open = useInspector((s) => s.open);
  const presentation = useInspector((s) => s.presentation);
  const breadcrumbs = useInspector((s) => s.breadcrumbs);
  const close = useInspector((s) => s.close);
  const studioPanelRenderer = useInspector((s) => s.studioPanelRenderer);
  const backToProjectId = useInspector((s) => s.backToProjectId);

  const subject = stack[stack.length - 1];
  const shouldRender = presentationMatchesFilter(presentation, presentationFilter);

  const studioPanel = React.useMemo(() => {
    if (!shouldRender || !subject || !studioPanelRenderer) return null;
    return studioPanelRenderer(subject);
  }, [shouldRender, subject, studioPanelRenderer]);

  const { data: backProject } = useProjectById({
    projectId: backToProjectId,
    enabled: shouldRender && stack.length === 1,
  });

  const backToProject = React.useMemo(() => {
    if (!backProject) return undefined;
    return {
      id: backProject.id,
      name: backProject.name,
      onReturn: () => close(),
    };
  }, [backProject, close]);

  if (!shouldRender || !subject) return null;

  return (
    <Inspector
      subject={subject}
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          if (subject.kind === 'image') {
            useImageDialogStore.getState().close();
          }
          if (subject.kind === 'document') {
            useDocumentDialogStore.getState().close();
          }
          if (subject.kind === 'video') {
            useVideoDialogStore.getState().close();
          }
          if (subject.kind === 'strategy') {
            useStrategyDialogStore.getState().close();
          }
          if (subject.kind === 'brand_guidelines') {
            useBrandGuidelinesDialogStore.getState().close();
          }
          close();
        }
      }}
      presentation={presentation}
      breadcrumbs={breadcrumbs}
      studioPanel={studioPanel ?? undefined}
      backToProject={backToProject}
    />
  );
};
